/** Per-tab return path (sessionStorage); Redux persist is shared across tabs so each tab keeps its own URL here. */

export function isSafeReturnPath(path: string): boolean {
  if (!path || path.length > 4096) return false
  return path.startsWith('/') && !path.startsWith('//')
}

/**
 * Path (pathname + search + hash) to restore after login. Same-origin only.
 *
 * Returns null for pages it makes no sense to send a user back to:
 *
 *  - `/logout` — restoring it would drop them straight back onto the logout
 *    screen after signing in.
 *  - `/error/*` — restoring an error page recreates the dead end. Logging out
 *    from Access Denied captured `/error/user-not-found`, so the next sign-in
 *    returned the user there without even reaching the app, replacing one
 *    inescapable loop with another.
 */
export function getPostLogoutCapturePath(): string | null {
  const path =
    window.location.pathname + window.location.search + window.location.hash
  if (path === '/logout' || path.startsWith('/logout?')) {
    return null
  }
  if (
    path === '/error' ||
    path.startsWith('/error/') ||
    path.startsWith('/error?')
  ) {
    return null
  }
  return path
}

export function createPostLogoutReturnPathHelpers(sessionStorageKey: string) {
  function getSessionPostLogoutReturnPath(): string | null {
    try {
      const v = sessionStorage.getItem(sessionStorageKey)
      return v && isSafeReturnPath(v) ? v : null
    } catch {
      return null
    }
  }

  function setSessionPostLogoutReturnPath(path: string | null): void {
    try {
      if (!path) {
        sessionStorage.removeItem(sessionStorageKey)
      } else {
        sessionStorage.setItem(sessionStorageKey, path)
      }
    } catch {
      /* ignore */
    }
  }

  function clearSessionPostLogoutReturnPath(): void {
    setSessionPostLogoutReturnPath(null)
  }

  return {
    getPostLogoutCapturePath,
    isSafeReturnPath,
    getSessionPostLogoutReturnPath,
    setSessionPostLogoutReturnPath,
    clearSessionPostLogoutReturnPath,
  }
}
